// EditCustomers.tsx (full file)
import React, { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import MenuItem from '@mui/material/MenuItem';
import { Grid } from '@mui/material';

// Icons
import SaveIcon from '@mui/icons-material/Save';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';

// Components
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import ContentCardsContainer, { ContentCard, CardType, CardTab, HeaderTabs } from 'src/components/organisms/ContentCard';
import { FormContainerMolecule } from 'src/components/molecules/formik/FormContainerMolecule';
import { FormField } from 'src/components/molecules/formik/FormField';
import CustomButton from 'src/components/atoms/buttons/CustomButton';

// Hooks & Utils
import useSubmit from 'src/hooks/forms/useSubmit';
import FormHelper from 'src/utils/formHelper';
import { buildSupplierRequest } from 'src/utils/submit/submitRequests';
import { useCustomerForm } from 'src/hooks/forms/useForms';
import AddressStreetAutocompleteField from 'src/components/molecules/formik/AddressStreetAutocompleteField';
import { useCustomerOptions } from 'src/hooks/forms/useOptions';

const schema = Yup.object().shape({

});

const defaultInitialValues: any = {};

function EditCustomers() {
  const [editId, PageName, currentSubmitFunction] =
    FormHelper.getEditPageConfig('Customer', buildSupplierRequest, buildSupplierRequest);

  const initialValues = useCustomerForm(editId, defaultInitialValues);
  const [result, submit] = useSubmit(currentSubmitFunction);

  const [currencyOptions,categoryOptions,discountCategoryOptions,shippingMethodOptions,shippingTermsOptions,
    paymentMethodOptions,paymentTermsOptions,salesPriceListOptions,salesChannelOptions,customerTypeOptions] = useCustomerOptions();

  const tabs: CardTab[] = useMemo(
    () => [
      { value: 'Defaults', label: 'Order Defaults' },
      { value: 'Legal', label: 'Legal Address' },
      { value: 'Billing', label: 'Billing Address' },
      { value: 'System', label: 'System Info' },
    ],
    [],
  );

  const [currentTab, setCurrentTab] = useState<string>('Defaults');

  const AddressTab = ({ props, prefix }: any) => (
    <FormContainerMolecule formikProps={props}>
      <FormField fieldProps={{ name: `${prefix}.Id`, disabled: true, label: 'Address Id', md: 3.8 }} formikProps={props} />
      <FormField fieldProps={{ name: `${prefix}.Company`, label: 'Company / Location Name', md: 8 }} formikProps={props} />

      <FormField fieldProps={{ name: `${prefix}.Country.Key`, disabled: true, label: 'Country Id', md: 3.8 }} formikProps={props} />
      <FormField fieldProps={{ name: `${prefix}.Country.Value`, label: 'Country', md: 5.8 }} formikProps={props} />

      <AddressStreetAutocompleteField
        formikProps={props}
        config={{
          streetField: `${prefix}.Street`,
          countryField: `${prefix}.Country.Value`,
          cityField: `${prefix}.City`,
          stateField: `${prefix}.State`,
          postcodeField: `${prefix}.Postcode`,
        }}
        md={7.8}
        xs={5.8}
        label="Street Address"
      />

      <FormField fieldProps={{ name: `${prefix}.StreetAdditionalInformation`, label: 'Unit / Suite', md: 3.8 }} formikProps={props} />

      <FormField fieldProps={{ name: `${prefix}.City`, label: 'City', md: 3.8 }} formikProps={props} />
      <FormField fieldProps={{ name: `${prefix}.State`, label: 'State', md: 3.8 }} formikProps={props} />
      <FormField fieldProps={{ name: `${prefix}.Postcode`, label: 'Postcode', md: 3.8 }} formikProps={props} />

      <FormField fieldProps={{ name: `${prefix}.Department`, label: 'Department', md: 5.8 }} formikProps={props} />
      <FormField fieldProps={{ name: `${prefix}.Contact`, label: 'Contact', md: 5.8 }} formikProps={props} />

      <FormField fieldProps={{ name: `${prefix}.EmailAddress`, label: 'General Email', md: 5.8 }} formikProps={props} />
      <FormField fieldProps={{ name: `${prefix}.PhoneNumber`, label: 'General Phone', md: 5.8 }} formikProps={props} />
      <FormField fieldProps={{ name: `${prefix}.MobileNumber`, label: 'Mobile', md: 5.8 }} formikProps={props} />
      <FormField fieldProps={{ name: `${prefix}.WebAddress`, label: 'Web Address', md: 5.8 }} formikProps={props} />

      <FormField fieldProps={{ name: `${prefix}.DoorCode`, label: 'Notes / Door Code', md: 12 }} formikProps={props} />
      <FormField fieldProps={{ name: `${prefix}.AdditionalInformation`, label: 'Additional Information', md: 12, multiline: true, rows: 3 }} formikProps={props} />
    </FormContainerMolecule>
  );

  return (
    <>
      <Formik
        initialValues={initialValues}
        validateOnBlur={true}
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={submit}
        enableReinitialize
      >
        {(props): JSX.Element => (
          <div>
            <PageHeader
              title={PageName}
              initialValues={initialValues}
              subTitle="Create and edit Customer"
              components={[
                { component: <CustomButton isSubmitting={props.isSubmitting} onClick={props.handleSubmit} title="Save" icon={<SaveIcon />} /> },
                { isInGroupedMenu: true, component: <CustomButton variant="text" title="New Customer" icon={<AddTwoToneIcon />} actionUrl="/orders/customers/create" /> },
              ]}
              alertData={{ dataArray: (result ?? false) ? [result] : null }}
            />

            <ContentCardsContainer>
              {/* --- ROW 1: Two md=6 cards --- */}
              <ContentCard cardId="Customer Identity" md={6}>
                <FormContainerMolecule formikProps={props}>
                  

                  <FormField fieldProps={{ name: 'FirstName', label: 'First Name', md: 5.8 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'LastName', label: 'Last Name', md: 5.8 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'CompanyName', label: 'Company Name', md: 12 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'ExternalCustomerNr', label: 'External Customer Nr', md: 12 }} formikProps={props} />

                  <FormField fieldProps={{ name: 'CustomerTypeId', select: true, label: 'Customer Type', md: 5.8 }} formikProps={props}>
                    {customerTypeOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                  </FormField>

                  <FormField fieldProps={{ name: 'CustomerCategoryId', select: true, label: 'Customer Category', md: 5.8 }} formikProps={props}>
                    {categoryOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                  </FormField>

                  <FormField fieldProps={{ name: 'DiscountCategoryId', select: true, label: 'Discount Category', md: 12 }} formikProps={props}>
                    {discountCategoryOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                  </FormField>

                  <FormField fieldProps={{ name: 'Description', label: 'Description', md: 12, multiline: true, rows: 4 }} formikProps={props} />
                </FormContainerMolecule>
              </ContentCard>

              <ContentCard cardId="Status & Finance" md={6}>
                <FormContainerMolecule formikProps={props}>
                

                  <FormField fieldProps={{ name: 'OrganisationId', label: 'Organisation Id', md: 12, disabled: true }} formikProps={props} />

                  <FormField fieldProps={{ name: 'CorporateRegistrationNumber', label: 'Corporate Reg. Number', md: 5.8 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'VATNumber', label: 'VAT Number', md: 5.8 }} formikProps={props} />

                  <FormField fieldProps={{ name: 'CreditLimit', label: 'Credit Limit', type: 'number', md: 5.8 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'AvailableCredit', label: 'Available Credit', type: 'number', md: 5.8, disabled: true }} formikProps={props} />

                  <FormField fieldProps={{ name: 'SalesChannelId', select: true, label: 'Sales Channel', md: 5.8 }} formikProps={props}>
                    {salesChannelOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                  </FormField>

                  <FormField fieldProps={{ name: 'SalesPriceListId', select: true, label: 'Sales Price List', md: 5.8 }} formikProps={props}>
                    {salesPriceListOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                  </FormField>

                  <FormField fieldProps={{ name: 'SalesPriceListHasTaxIncluded', checkbox: true, label: 'Tax included', md: 5.8 }} formikProps={props} />

                  <FormField fieldProps={{ name: 'OrderDefaultCurrencyId', select: true, label: 'Default Currency', md: 5.8 }} formikProps={props}>
                    {currencyOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                  </FormField>
                  <FormField fieldProps={{ name: 'Synchronize', checkbox: true, label: 'Synchronize', md: 3.8 }} formikProps={props} />
                    <FormField fieldProps={{ name: 'Disabled', checkbox: true, label: 'Disabled', md: 3.8 }} formikProps={props} />
                    <FormField fieldProps={{ name: 'Blocked', checkbox: true, label: 'Blocked', md: 3.8 }} formikProps={props} />
                </FormContainerMolecule>
              </ContentCard>

              {/* --- Tab Container --- */}
              <ContentCard
                cardId="TabContainer"
                contentPadding={0}
                md={12}
                cardTypes={[CardType.tabContainer]}
                cardHeader={<HeaderTabs tabs={tabs} currentTab={currentTab} setCurrentTab={setCurrentTab} />}
              >
                {currentTab === 'Legal' && (
                  <ContentCard cardId="Legal" cardTypes={[CardType.tab]} contentPadding={2}>
                    <AddressTab props={props} prefix="LegalAddress" />
                  </ContentCard>
                )}

                {currentTab === 'Billing' && (
                  <ContentCard cardId="Billing" cardTypes={[CardType.tab]} contentPadding={2}>
                    <AddressTab props={props} prefix="BillingAddress" />
                  </ContentCard>
                )}

                {currentTab === 'Defaults' && (
                  <ContentCard cardId="Defaults" cardTypes={[CardType.tab]} contentPadding={2}>
                    <FormContainerMolecule formikProps={props}>
                      <FormField fieldProps={{ name: 'OrderDefaultShippingMethodId', select: true, label: 'Shipping Method', md: 5.8 }} formikProps={props}>
                        {shippingMethodOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                      </FormField>

                      <FormField fieldProps={{ name: 'OrderDefaultPaymentMethodId', select: true, label: 'Payment Method', md: 5.8 }} formikProps={props}>
                        {paymentMethodOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                      </FormField>

                      <FormField fieldProps={{ name: 'OrderDefaultShippingTermsId', select: true, label: 'Shipping Terms', md: 5.8 }} formikProps={props}>
                        {shippingTermsOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                      </FormField>

                      <FormField fieldProps={{ name: 'OrderDefaultPaymentTermsId', select: true, label: 'Payment Terms', md: 5.8 }} formikProps={props}>
                        {paymentTermsOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                      </FormField>

                      <FormField fieldProps={{ name: 'OrderDefaultInvoiceCreationModeId', label: 'Invoice Creation Mode', md: 3.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'OrderDefaultShippingTermsDestination', label: 'Ship Terms Destination', md: 7.8 }} formikProps={props} />
                    </FormContainerMolecule>
                  </ContentCard>
                )}

                {currentTab === 'System' && (
                  <ContentCard cardId="System" cardTypes={[CardType.tab]} contentPadding={2}>
                    <FormContainerMolecule formikProps={props}>
                      <FormField fieldProps={{ name: 'CreateDate', date: true, label: 'Created Date', md: 3.8, disabled: true }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ModifyDate', date: true, label: 'Last Modified', md: 3.8, disabled: true }} formikProps={props} />
                      <FormField fieldProps={{ name: 'CreatedBy', label: 'Created By', md: 5.8, disabled: true }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ModifiedBy', label: 'Modified By', md: 5.8, disabled: true }} formikProps={props} />
                    </FormContainerMolecule>
                  </ContentCard>
                )}
              </ContentCard>
            </ContentCardsContainer>
          </div>
        )}
      </Formik>

      <Footer />
    </>
  );
}

export default EditCustomers;
